import { PLAGUE_ENABLED } from "@/config/constants";
import type { ResourceType } from "@/game/entities/config/resources";
import { EmitterEvents } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import type { Reward, TileIndex } from "@/types/tile";
import { FrameName, tilerepoPoiFrameNames } from "../config";
import TileEntity, { type TileEntityWithOnRevealMethod } from "../TileEntity";

abstract class AbstractPoiTileEntity
	extends TileEntity
	implements TileEntityWithOnRevealMethod
{
	public static readonly frameName: FrameName;

	public onReveal(): void {
		if (!this.config.onReveal) {
			this.getMainPoiTile?.onReveal();
			return;
		}
		// eslint-disable-next-line no-console
		console.log("[poi] onReveal");

		if (!this.isFullyExplored) return;

		const { reward } = this.config;
		this.config.onReveal();

		if (reward !== undefined) {
			for (const [key, value] of Object.entries(
				reward as Reward,
			) as Array<[ResourceType, number]>) {
				useResourcesStore().increase(key, value, "poi reward");
			}

			if (Object.entries(reward as Reward).length > 0) {
				this.emitter.emit(EmitterEvents.PhaserTileClick, {
					x: this.x,
					y: this.y,
					reward,
				});
			}
		}
		this.tilerepo.handlePoiIndexesIncrement(this.index);

		useRuntimeStore().setPoiJustRevealed(true);
		this.emitter.emit(EmitterEvents.PhaserTileDetailOpen, this);

		const sm = useSegmentManager();
		sm.markSegmentAsDirtyByTileIndex(this.index);
		sm.markSegmentAsDirtyByTileIndex(543383); // Plagued forest POI, needs to be recalculated because of newly revealed POI.
		sm.run(true);
	}

	public getRewards(): Reward {
		const mainPoiTile = this.getMainPoiTile;
		return mainPoiTile?.config.reward || {};
	}

	public get storage(): Map<ResourceType, number> {
		const mainPoiTile = this.getMainPoiTile;
		return mainPoiTile?.config.storage || new Map<ResourceType, number>();
	}

	public get rewardText(): string[] {
		const mainPoiTile = this.getMainPoiTile;
		return mainPoiTile?.config.rewardText || [];
	}

	public get rewardTextReplace(): { [key: string]: string }[] {
		const mainPoiTile = this.getMainPoiTile;
		return mainPoiTile?.config.rewardTextReplace || [];
	}

	public get getMainPoiTile(): AbstractPoiTileEntity | null {
		const neighbors = this.neighbors;
		const mainPoiTileIndex = neighbors.findIndex(
			(tileIndex: number): boolean => {
				const tileEntity =
					this.tilerepo.getTileEntityByTileIndex(tileIndex);
				if (!tileEntity) return false;
				return (
					(tilerepoPoiFrameNames.has(tileEntity?.frameName) &&
						tileEntity?.frameName.endsWith("1.png")) ||
					false
				);
			},
		);
		if (mainPoiTileIndex === -1) return null;
		const mainPoiTile = this.tilerepo.getTileEntityByTileIndex(
			neighbors[mainPoiTileIndex],
		) as AbstractPoiTileEntity | null;
		return mainPoiTile;
	}

	private get neighbors(): Array<TileIndex> {
		return this.tilerepo.getNeighbourTileIndexesByTileCoordinates(
			{ x: this.x, y: this.y },
			3,
			true,
		);
	}

	public get isFullyExplored(): boolean {
		if (this.numberOfRevealedTiles < this.numberOfPoiTiles) return false;

		return true;
	}

	public get numberOfRevealedTiles(): number {
		const neighbors = this.neighbors;

		return neighbors.filter(
			(tileIndex: number): boolean =>
				this.tilerepo
					.getTileEntityByTileIndex(tileIndex)
					?.frameName.startsWith(this.poiFrameName) || false,
		).length;
	}

	public get numberOfPoiTiles(): number {
		return Object.values(FrameName).filter((frameName: string): boolean =>
			frameName.startsWith(this.poiFrameName),
		).length;
	}

	public get poiFrameName(): string {
		return this.frameName.replace(/\d+/g, "").replace(".png", "");
	}

	public get poiPlagueFrameName(): string {
		const plagueImages = [
			FrameName.PARTLY_DEMOLISHED_INN1,
			FrameName.MONASTERY_ABANDONED1,
			FrameName.MONASTERY_ABANDONED2,
			FrameName.MONASTERY_ABANDONED3,
			FrameName.MONASTERY_ABANDONED4,
		];
		return (
			this.poiFrameName +
			(PLAGUE_ENABLED && plagueImages.includes(this.frameName)
				? "Plague_"
				: "")
		);
	}
}

export default AbstractPoiTileEntity;
